import {Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {map, Observable, take} from 'rxjs';
import {DmxDocumentService} from './http/dmx-document.service';
import {DocumentModel, SavedDocumentModel, Status, Workflow} from '../model/document.model';
import {StatusCodes} from '../model/constants';

/**
 * Speichert einen Datensatz, ohne durch das Veröffentlichungsmenü zu gehen.
 *
 * Fachlich ist das derselbe Vorgang wie das Veröffentlichen: der Rest-Service kennt nur
 * `POST {rfa}`, und **der Status im Header entscheidet**, was daraus wird. Das
 * Veröffentlichungsmenü (`DocumentDetailActionComponent`) setzt ihn über die Statusliste;
 * hier wird er direkt auf "Speichern" gesetzt, damit Knopf und Tastenkürzel den Umweg
 * über den Dialog sparen.
 *
 * Der Datensatz landet damit in derselben Tabelle und erscheint in der Liste der RFA –
 * es gibt keinen zweiten Speicherweg.
 */
@Injectable({providedIn: 'root'})
export class DocumentSaveService {

  /** Status "Speichern" – der Rest-Service leitet daraus StatusEnum.SAVED ab. */
  private static readonly STATUS_SPEICHERN: Status = {
    code: 1,
    kuerzel: 'Speichern',
    description: 'Datensatz gespeichert',
    icon: 'save'
  };

  /**
   * Workflow "Alles" (`WorkflowEnum.WDR_ALLES`, Ausspielwege firebase-database/videotext/online).
   *
   * Der Rest-Service braucht beim Speichern `header.workflow.ausspiel`, ein neues Dokument
   * bringt aber keinen Workflow mit – das Template lässt das Feld leer. Auf der Grafik-Seite
   * gilt als Sonderfall **immer** "Alles", statt den Redakteur erst durch die Workflow-Auswahl
   * des Veröffentlichungsmenüs zu schicken.
   *
   * Es genügt der `code`: `Workflow.WorkflowDeserializer` im Rest-Service liest **nur** ihn und
   * baut Name, Kürzel, Beschreibung und Ausspielwege selbst aus `WorkflowEnum` auf. Deshalb
   * steht hier bewusst kein aus der Metadaten-Liste geholtes Objekt – das würde nur einen
   * asynchronen Ladeschritt in den Speichervorgang einbauen, dessen Ergebnis der Server ohnehin
   * verwirft.
   */
  public static readonly WORKFLOW_ALLES: Workflow = {
    code: 20,
    kuerzel: 'Alles',
    description: 'Alle Ausspielwege',
    icon: 'send'
  };

  /** Kein Injizieren: StatusCodes ist eine reine Konstantenklasse ohne Provider. */
  private readonly statusCodes = new StatusCodes();

  constructor(private readonly restService: DmxDocumentService) {
  }

  /**
   * Setzt Status, Zeitstempel und ggf. den Workflow und schickt das Dokument an den Rest-Service.
   *
   * @param rfaKuerzel  Kürzel der RFA, z.B. "wdr"
   * @param document    das Dokument, so wie es gerade auf der Seite steht – die
   *                    Element-Komponenten schreiben ihre Werte direkt in diesen Baum,
   *                    es muss also nichts eingesammelt werden
   */
  public speichern(rfaKuerzel: string, document: DocumentModel): Observable<SavedDocumentModel> {
    document.header.status = DocumentSaveService.STATUS_SPEICHERN;
    document.header.updated = new Date();
    if (!document.header.workflow) {
      document.header.workflow = DocumentSaveService.WORKFLOW_ALLES;
    }
    return this.restService.publish(rfaKuerzel, document).pipe(
      take(1),
      map(response => response.body as SavedDocumentModel)
    );
  }

  /**
   * Lesbare Meldung zu einem fehlgeschlagenen Speichern.
   *
   * Angulars `HttpErrorResponse.message` endet auf **"500 OK"** – das liest sich wie eine
   * Erfolgsmeldung und ist der Grund, warum diese Methode existiert. Das "OK" stammt nicht
   * vom Server: schickt der keine Reason-Phrase mit, ist `xhr.statusText` leer, und
   * Angulars `HttpXhrBackend` setzt dann pauschal `'OK'` ein. Die Meldung wird deshalb
   * selbst gebaut – aus dem Statuscode und dem Text, den der Rest-Service im Body mitgibt
   * (bei einem Persistence-Fehler nennt der PostgREST als Ursache).
   */
  public static fehlermeldung(fehler: unknown): string {
    if (!(fehler instanceof HttpErrorResponse)) {
      return String(fehler);
    }
    if (fehler.status === 0) {
      return 'Rest-Service nicht erreichbar (keine Antwort).';
    }
    const grund = DocumentSaveService.serverText(fehler.error);
    return grund ? `Statuscode ${fehler.status} – ${grund}` : `Statuscode ${fehler.status}`;
  }

  /** Der Text, den der Server im Body mitgeschickt hat – als Klartext oder JSON-Feld. */
  private static serverText(body: unknown): string {
    if (typeof body === 'string') {
      return body.trim();
    }
    if (body && typeof body === 'object') {
      const felder = body as { message?: unknown; error?: unknown };
      if (typeof felder.message === 'string') {
        return felder.message.trim();
      }
      if (typeof felder.error === 'string') {
        return felder.error.trim();
      }
    }
    return '';
  }

  /** Ist dieser Status "gespeichert" (im Gegensatz zu veröffentlicht)? */
  public istGespeichert(status?: Status): boolean {
    return status?.code === this.statusCodes.SAVE;
  }
}
